  const cloneCellData = <T,>(data: T): T => {
    return JSON.parse(JSON.stringify(data));
  };

  const arrangeTopLevelNodes = (nodes: any[], parentCopyId: string) => {
    const nodeMap = new Map<string, any>();
    nodes.forEach(n => nodeMap.set(n.id, n));

    const moveNodeAndChildren = (nodeId: string, dx: number, dy: number) => {
      const node = nodeMap.get(nodeId);
      if (!node) return;
      node.x += dx;
      node.y += dy;
      nodes.forEach(child => {
        if (child.data?.parentId === nodeId) {
          moveNodeAndChildren(child.id, dx, dy);
        }
      });
    };

    const parentNode = nodeMap.get(parentCopyId);
    const centerX = parentNode ? parentNode.x + parentNode.width / 2 : 400;
    const centerY = parentNode ? parentNode.y + parentNode.height / 2 : 300;

    const topLevelNodes = nodes.filter(
      n => !n.data?.parentId && n.id !== parentCopyId,
    );
    if (topLevelNodes.length === 0) return;

    if (topLevelNodes.length === 1) {
      const n = topLevelNodes[0];
      const newX = centerX + (parentNode?.width || 200) / 2 + 160;
      const newY = centerY - n.height / 2;
      moveNodeAndChildren(n.id, newX - n.x, newY - n.y);
      return;
    }

    const parentRadius = Math.max(parentNode?.width ?? 200, parentNode?.height ?? 200) / 2;
    const radius = parentRadius + 200;

    topLevelNodes.forEach((node, idx) => {
      const angle = (2 * Math.PI * idx) / topLevelNodes.length;
      const newX = centerX + radius * Math.cos(angle) - node.width / 2;
      const newY = centerY + radius * Math.sin(angle) - node.height / 2;
      moveNodeAndChildren(node.id, newX - node.x, newY - node.y);
    });
  };

import { useState, useCallback, useRef } from 'react';
import type { Graph } from '@antv/x6';
import type { SubGraph } from '../types';
import { applyInvocationZigzag, applyInvocationZigzagToEdge } from '../utils/invocationZigzag';
import { setSuppressManualVertices } from './useNodeInteractions';

/**
 * 子图状态管理 Hook
 *
 * 负责：
 * 1. 管理子图树结构
 * 2. 序列化/反序列化图数据
 * 3. 创建子图（提取节点及其连接的邻居）
 * 4. 在子图之间导航
 */
export function useSubGraph(graphRef: React.MutableRefObject<Graph | null>) {
  // 使用 ref 存储核心状态，避免闭包陈旧引用
  const subGraphTreeRef = useRef<SubGraph | null>(null);
  const currentGraphIdRef = useRef<string>('root');
  const graphPathRef = useRef<SubGraph[]>([]);

  // 使用 state 触发渲染更新
  const [, forceUpdate] = useState({});

  // 用于生成唯一 ID
  const idCounterRef = useRef(0);
  const generateId = () => {
    idCounterRef.current += 1;
    return `subgraph-${idCounterRef.current}`;
  };

  /**
   * 序列化当前图的所有节点和边
   */
  const serializeCurrentGraph = useCallback(() => {
    const graph = graphRef.current;
    if (!graph) return { nodes: [], edges: [] };

    const nodes = graph.getNodes().map(node => {
      const pos = node.getPosition();
      const size = node.getSize();
      return {
        id: node.id,
        shape: node.shape,
        x: pos.x,
        y: pos.y,
        width: size.width,
        height: size.height,
        zIndex: typeof node.getZIndex === 'function' ? node.getZIndex() : (node as any).zIndex,
        data: { ...node.getData() },
      };
    });

    const edges = graph.getEdges().map(edge => {
      const source = edge.getSource();
      const target = edge.getTarget();
      return {
        id: edge.id,
        shape: edge.shape,
        source: typeof source === 'string' ? { cell: source } : source,
        target: typeof target === 'string' ? { cell: target } : target,
        zIndex: typeof edge.getZIndex === 'function' ? edge.getZIndex() : (edge as any).zIndex,
        data: { ...edge.getData() },
        attrs: edge.getAttrs(),
      };
    });

    return { nodes, edges };
  }, [graphRef]);

  /**
   * 在树中查找指定的子图
   */
  const findSubGraph = useCallback((root: SubGraph | null, id: string): SubGraph | null => {
    if (!root) return null;
    if (root.id === id) return root;
    for (const child of root.children) {
      const found = findSubGraph(child, id);
      if (found) return found;
    }
    return null;
  }, []);

  /**
   * 更新树中的指定子图
   */
  const updateSubGraph = useCallback((
    root: SubGraph,
    id: string,
    updater: (node: SubGraph) => SubGraph
  ): SubGraph => {
    if (root.id === id) return updater(root);
    return {
      ...root,
      children: root.children.map(child => updateSubGraph(child, id, updater)),
    };
  }, []);

  /**
   * 构建从根到指定子图的路径
   */
  const buildPath = useCallback((root: SubGraph | null, targetId: string): SubGraph[] => {
    if (!root) return [];
    if (root.id === targetId) return [root];

    for (const child of root.children) {
      const path = buildPath(child, targetId);
      if (path.length > 0) return [root, ...path];
    }
    return [];
  }, []);

  /**
   * 初始化根图
   */
  const initializeRootGraph = useCallback((initialNodes: any[], initialEdges: any[]) => {
    const root: SubGraph = {
      id: 'root',
      name: '主图',
      parentNodeId: '',
      parentGraphId: null,
      nodeType: 'object',
      nodes: initialNodes,
      edges: initialEdges,
      children: [],
    };
    subGraphTreeRef.current = root;
    currentGraphIdRef.current = 'root';
    graphPathRef.current = [root];
    forceUpdate({});
  }, [forceUpdate]);

  /**
   * 创建子图
   *
   * 逻辑：
   * 1. 找到指定节点，复制一份作为子图父节点
   * 2. 收集直接连接的边和一层邻居
   * 3. 同类邻居（Object↔Object, Process↔Process）→ 内嵌子节点（有几个复制几个）
   * 4. 始终新建 2 个同类型内嵌子节点
   * 5. 异类邻居 → 外围独立节点
   * 6. 所有子图节点使用唯一 ID，与主图完全隔离
   * 7. 保存主图状态到树中，然后导航到子图
   */
  const createSubGraph = useCallback((nodeId: string) => {
    const graph = graphRef.current;
    // 使用 ref 获取最新的树和当前图 ID，避免闭包陈旧引用
    const latestTree = subGraphTreeRef.current;
    const latestCurrentGraphId = currentGraphIdRef.current;
    if (!graph || !latestTree) return;

    const targetNode = graph.getCellById(nodeId);
    if (!targetNode || !targetNode.isNode()) return;

    const nodeType = targetNode.getData()?.type as string;
    const nodeLabel = targetNode.getData()?.label || 'Node';
    const targetPos = targetNode.getPosition();
    const targetSize = targetNode.getSize();

    // 收集嵌入子节点 ID，用于排除
    const embeddedChildIds = new Set<string>();
    (targetNode.getChildren() || []).forEach((child: any) => {
      if (child.isNode()) embeddedChildIds.add(child.id);
    });

    // 只找直接连接到目标节点的边（按 edge.id 去重）
    const seenEdgeIds = new Set<string>();
    const directEdges = graph.getEdges().filter(edge => {
      if (seenEdgeIds.has(edge.id)) return false;
      const sid = (edge.getSource() as any)?.cell;
      const tid = (edge.getTarget() as any)?.cell;
      if (sid === nodeId || tid === nodeId) {
        seenEdgeIds.add(edge.id);
        return true;
      }
      return false;
    });

    // 收集一层邻居（排除自身和嵌入子节点）
    const neighborIds = new Set<string>();
    directEdges.forEach(edge => {
      const sid = (edge.getSource() as any)?.cell;
      const tid = (edge.getTarget() as any)?.cell;
      if (sid === nodeId && tid && tid !== nodeId && !embeddedChildIds.has(tid)) neighborIds.add(tid);
      if (tid === nodeId && sid && sid !== nodeId && !embeddedChildIds.has(sid)) neighborIds.add(sid);
    });

    // 将邻居分类：同类节点（放入内部）vs 异类节点（外围）
    const sameTypeNeighbors: { id: string; node: any }[] = [];
    const otherNeighbors: { id: string; node: any }[] = [];

    neighborIds.forEach(nid => {
      const neighbor = graph.getCellById(nid);
      if (!neighbor || !neighbor.isNode()) return;
      const nType = neighbor.getData()?.type;
      if (nType === nodeType) {
        sameTypeNeighbors.push({ id: nid, node: neighbor });
      } else {
        otherNeighbors.push({ id: nid, node: neighbor });
      }
    });

    // === 子图 ID 前缀，确保与主图完全隔离 ===
    const sgPrefix = `sg-${generateId()}`;
    // 建立原始 ID → 子图 ID 的映射表
    const idMap = new Map<string, string>();
    const parentCopyId = `${sgPrefix}-${nodeId}`;
    idMap.set(nodeId, parentCopyId);

    const cloneNeighborStates = (originalNode: any, parentCopyId: string) => {
      const stateCopies: any[] = [];
      (originalNode.getChildren() || []).forEach((child: any) => {
        if (!child?.isNode?.()) return;
        if (child.getData()?.type !== 'state') return;
        const stateId = `${sgPrefix}-${child.id}`;
        idMap.set(child.id, stateId);
        const childPos = child.getPosition();
        const childSize = child.getSize();
        stateCopies.push({
          id: stateId,
          shape: child.shape,
          x: childPos.x - targetPos.x + 400,
          y: childPos.y - targetPos.y + 300,
          width: childSize.width,
          height: childSize.height,
          data: {
            ...child.getData(),
            parentId: parentCopyId,
            originalId: child.id,
            selected: false,
          },
        });
      });
      return stateCopies;
    };

    // === 构建子图节点列表 ===
    const subGraphNodes: any[] = [];

    // 父节点副本尺寸：只容纳新增的 2 个子节点（同类邻居不再放入内部）
    const innerPadding = 24;
    const innerSizeMap: Record<string, { width: number; height: number }> = {
      object: { width: 180, height: 80 },
      option: { width: 180, height: 80 },
      process: { width: 140, height: 100 },
    };
    const innerSize = innerSizeMap[nodeType] || {
      width: targetSize.width,
      height: targetSize.height,
    };
    const innerNodeW = innerSize.width;
    const innerNodeH = innerSize.height;
    const innerCount = 2; // 固定新增 2 个子节点
    const neededInnerWidth = innerCount * innerNodeW + (innerCount + 1) * innerPadding;
    const copyWidth = Math.max(targetSize.width, neededInnerWidth + innerPadding * 2);
    const copyHeight = targetSize.height + innerNodeH + innerPadding * 2;

    // 1. 当前节点副本（居中）
    subGraphNodes.push({
      id: parentCopyId,
      shape: targetNode.shape,
      x: 400,
      y: 300,
      width: copyWidth,
      height: copyHeight,
      data: {
        ...targetNode.getData(),
        label: `${nodeLabel} (子图)`,
        selected: false,
        childStateCount: innerCount,
        originalId: nodeId,
        isParentCopy: true,
      },
    });

    // 2. 新建 2 个与当前节点同类型的子节点（放在父节点副本内部）
    const innerNodes: any[] = [];
    for (let i = 1; i <= 2; i++) {
      innerNodes.push({
        id: `${sgPrefix}-inner-${i}`,
        shape: targetNode.shape,
        w: innerNodeW,
        h: innerNodeH,
        data: {
          ...targetNode.getData(),
          label: `${nodeLabel}-${i}`,
          selected: false,
          parentId: parentCopyId,
          childStateCount: 0,
        },
      });
    }

    // 计算内嵌节点位置（在父副本内部水平排列，靠近底部）
    const totalInnerW = innerNodes.reduce((sum, n) => sum + n.w, 0) + (innerNodes.length + 1) * innerPadding;
    let innerStartX = 400 + (copyWidth - totalInnerW) / 2 + innerPadding;
    const innerY = 300 + copyHeight - innerNodeH - innerPadding;

    innerNodes.forEach(n => {
      subGraphNodes.push({
        id: n.id,
        shape: n.shape,
        x: innerStartX,
        y: innerY,
        width: n.w,
        height: n.h,
        data: n.data,
      });
      innerStartX += n.w + innerPadding;
    });

    // 3. 同类邻居 → 作为独立节点放在子图画布中（不嵌入父节点内部）
    sameTypeNeighbors.forEach(item => {
      const sgId = `${sgPrefix}-${item.id}`;
      idMap.set(item.id, sgId);
      const nPos = item.node.getPosition();
      const nSize = item.node.getSize();
      subGraphNodes.push({
        id: sgId,
        shape: item.node.shape,
        x: nPos.x - targetPos.x + 400,
        y: nPos.y - targetPos.y + 300,
        width: nSize.width,
        height: nSize.height,
        data: {
          ...item.node.getData(),
          selected: false,
          originalId: item.id,
        },
      });
      subGraphNodes.push(...cloneNeighborStates(item.node, sgId));
    });

    // 4. 异类邻居节点（使用唯一 ID，保持相对位置）
    otherNeighbors.forEach(item => {
      const sgId = `${sgPrefix}-${item.id}`;
      idMap.set(item.id, sgId);
      const nPos = item.node.getPosition();
      const nSize = item.node.getSize();
      subGraphNodes.push({
        id: sgId,
        shape: item.node.shape,
        x: nPos.x - targetPos.x + 400,
        y: nPos.y - targetPos.y + 300,
        width: nSize.width,
        height: nSize.height,
        data: {
          ...item.node.getData(),
          selected: false,
          originalId: item.id,
        },
      });
      subGraphNodes.push(...cloneNeighborStates(item.node, sgId));
    });

    // 将顶层节点按照环形展开，避免遮挡
    arrangeTopLevelNodes(subGraphNodes, parentCopyId);

    // === 构建子图边列表（所有引用通过 idMap 映射到子图 ID） ===
    const subGraphEdges = directEdges.map(edge => {
      const source = edge.getSource();
      const target = edge.getTarget();
      const sid = (source as any)?.cell;
      const tid = (target as any)?.cell;

      const newSource = { ...source, cell: idMap.get(sid) || sid };
      const newTarget = { ...target, cell: idMap.get(tid) || tid };

      return {
        id: `${sgPrefix}-${edge.id}`,
        shape: edge.shape,
        source: newSource,
        target: newTarget,
        data: { ...edge.getData() },
        attrs: edge.getAttrs(),
      };
    });

    // === 创建新子图 ===
    const newSubGraphId = generateId();
    const newSubGraph: SubGraph = {
      id: newSubGraphId,
      name: `${nodeLabel} > 子图`,
      parentNodeId: nodeId,
      parentGraphId: latestCurrentGraphId,
      nodeType: nodeType as 'object' | 'process' | 'option',
      nodes: subGraphNodes.map(node => ({ ...node, data: { ...node.data } })),
      edges: subGraphEdges.map(edge => ({ ...edge, data: { ...edge.data } })),
      children: [],
    };

    // === 更新树：保存主图状态 + 添加子图 + 标记 hasSubGraph ===
    const { nodes: savedNodes, edges: savedEdges } = serializeCurrentGraph();

    console.log('[createSubGraph] 保存前 - latestCurrentGraphId:', latestCurrentGraphId);
    console.log('[createSubGraph] 保存前 - 主图节点数:', savedNodes.length);
    console.log('[createSubGraph] 新建子图 ID:', newSubGraphId);
    console.log('[createSubGraph] 新建子图节点数:', newSubGraph.nodes.length);

    const updatedTree = updateSubGraph(latestTree, latestCurrentGraphId, g => {
      console.log('[createSubGraph] updateSubGraph - 更新节点:', g.id);
      console.log('[createSubGraph] updateSubGraph - 原有 children 数:', g.children.length);
      return {
        ...g,
        nodes: savedNodes.map(n =>
          n.id === nodeId
            ? { ...n, data: { ...n.data, hasSubGraph: true, subGraphId: newSubGraphId } }
            : n
        ),
        edges: savedEdges,
        children: [...g.children, newSubGraph],
      };
    });

    console.log('[createSubGraph] 更新后 - root 节点数:', updatedTree.nodes.length);
    console.log('[createSubGraph] 更新后 - root children 数:', updatedTree.children.length);
    updatedTree.children.forEach((child, i) => {
      console.log(`[createSubGraph] 更新后 - child[${i}] id:`, child.id, '节点数:', child.nodes.length);
    });

    subGraphTreeRef.current = updatedTree;

    // 调试：记录子图节点数据
    console.log('[createSubGraph] 子图节点:', subGraphNodes.map(n => ({ id: n.id, label: n.data.label })));

    // === 导航到子图：清空画布，加载子图数据 ===
    // 使用 removeCells 而不是 clearCells，确保彻底清空
    const allCells = graph.getCells();
    if (allCells.length > 0) {
      graph.removeCells(allCells);
    }
    newSubGraph.nodes.forEach(n => graph.addNode(cloneCellData(n)));

    // 嵌入子节点到父副本内
    newSubGraph.nodes.forEach(n => {
      if (n.data?.parentId) {
        const parent = graph.getCellById(n.data.parentId);
        const child = graph.getCellById(n.id);
        if (parent?.isNode() && child?.isNode()) {
          parent.embed(child);
          parent.toBack();
          child.toFront();
        }
      }
    });

    // 父 process 副本的 body 设置 pointer-events: none，让鼠标事件穿透到子节点
    newSubGraph.nodes.forEach(n => {
      if (n.data?.isParentCopy) {
        const parentCell = graph.getCellById(n.id);
        if (parentCell?.isNode?.()) {
          parentCell.attr('body/pointerEvents', 'none');
        }
      }
    });

    newSubGraph.edges.forEach((e: any) => graph.addEdge(cloneCellData(e)));

    setSuppressManualVertices(true);
    graph.getEdges().forEach(edge => {
      const edgeData = edge.getData?.() || {};
      if (edgeData.edgeSubtype === 'invocation') {
        edge.setData({ ...edgeData, _manualVertices: false });
        applyInvocationZigzagToEdge(edge, graph, true);
      }
    });
    setSuppressManualVertices(false);

    currentGraphIdRef.current = newSubGraphId;
    graphPathRef.current = buildPath(updatedTree, newSubGraphId);
    forceUpdate({});

    setTimeout(() => {
      graph.zoomToFit({ padding: 60, maxScale: 1.2 });
    }, 100);
  }, [graphRef, serializeCurrentGraph, updateSubGraph, buildPath, generateId]);

  /**
   * 导航到指定子图
   *
   * 逻辑：
   * 1. 序列化当前图并保存
   * 2. 更新当前图 ID
   * 3. 清空画布
   * 4. 加载目标图的节点和边
   * 5. 更新路径
   */
  const navigateToGraph = useCallback((targetId: string, tree?: SubGraph | null) => {
    const graph = graphRef.current;
    // 使用 ref 获取最新的树和当前图 ID，避免闭包陈旧引用
    const latestTree = tree ?? subGraphTreeRef.current;
    const latestCurrentGraphId = currentGraphIdRef.current;

    if (!graph || !latestTree) return;

    console.log('[navigateToGraph] === 开始导航 ===');
    console.log('[navigateToGraph] 从', latestCurrentGraphId, '导航到', targetId);
    console.log('[navigateToGraph] 当前画布节点数:', graph.getNodes().length);

    // 序列化并保存当前图
    const { nodes: currentNodes, edges: currentEdges } = serializeCurrentGraph();
    console.log('[navigateToGraph] 序列化当前图节点数:', currentNodes.length);

    const treeWithSaved = updateSubGraph(latestTree, latestCurrentGraphId, current => {
      console.log('[navigateToGraph] updateSubGraph - 保存节点:', current.id);
      return {
        ...current,
        nodes: currentNodes,
        edges: currentEdges,
      };
    });

    // 找到目标图
    const targetGraph = findSubGraph(treeWithSaved, targetId);
    if (!targetGraph) {
      console.log('[navigateToGraph] 未找到目标图:', targetId);
      return;
    }

    console.log('[navigateToGraph] 目标图节点数:', targetGraph.nodes.length);
    console.log('[navigateToGraph] 目标图节点:', targetGraph.nodes.map(n => ({ id: n.id, label: n.data.label })));

    // 清空画布：使用 removeCells 确保彻底清空
    const allCells = graph.getCells();
    if (allCells.length > 0) {
      graph.removeCells(allCells);
    }

    // 加载目标图的节点
    const nodesToRender = targetGraph.nodes;
    nodesToRender.forEach(nodeData => {
      graph.addNode(cloneCellData(nodeData));
    });

    // 恢复父子关系（状态/值节点嵌入对象节点）
    nodesToRender.forEach(nodeData => {
      const data = nodeData.data;
      if (data.parentId) {
        const parentNode = graph.getCellById(data.parentId);
        const childNode = graph.getCellById(nodeData.id);
        if (parentNode?.isNode() && childNode?.isNode()) {
          parentNode.embed(childNode);
          parentNode.toBack();
          childNode.toFront();
        }
      }
    });

    // 父 process 副本的 body 设置 pointer-events: none，让鼠标事件穿透到子节点
    nodesToRender.forEach(nodeData => {
      if (nodeData.data?.isParentCopy) {
        const parentCell = graph.getCellById(nodeData.id);
        if (parentCell?.isNode?.()) {
          parentCell.attr('body/pointerEvents', 'none');
        }
      }
    });

    // 加载目标图的边
    const edgesToRender = targetGraph.edges;
    edgesToRender.forEach(edgeData => {
      graph.addEdge(cloneCellData(edgeData));
    });

    setSuppressManualVertices(true);
    graph.getEdges().forEach(edge => {
      const edgeData = edge.getData?.() || {};
      if (edgeData.edgeSubtype === 'invocation') {
        edge.setData({ ...edgeData, _manualVertices: false });
        applyInvocationZigzagToEdge(edge, graph, true);
      }
    });
    setSuppressManualVertices(false);

    // 更新状态
    subGraphTreeRef.current = treeWithSaved;
    currentGraphIdRef.current = targetId;
    graphPathRef.current = buildPath(treeWithSaved, targetId);
    forceUpdate({});

    // 调整视图
    setTimeout(() => {
      graph.zoomToFit({ padding: 60, maxScale: 1.2 });
    }, 100);
  }, [graphRef, serializeCurrentGraph, findSubGraph, updateSubGraph, buildPath, forceUpdate]);

  /**
   * 获取当前图的信息
   */
  const getCurrentGraph = useCallback(() => {
    return findSubGraph(subGraphTreeRef.current, currentGraphIdRef.current);
  }, [findSubGraph]);

  return {
    subGraphTree: subGraphTreeRef.current,
    currentGraphId: currentGraphIdRef.current,
    graphPath: graphPathRef.current,
    initializeRootGraph,
    createSubGraph,
    navigateToGraph,
    getCurrentGraph,
    serializeCurrentGraph,
  };
}
